import { Vec3 } from '../types';

export class Camera {
  position: Vec3;
  yaw: number;    // Rotation around Y axis (left/right)
  pitch: number;  // Rotation around X axis (up/down)

  private readonly maxPitch = Math.PI / 2 - 0.01;  // ~89 degrees
  private readonly minPitch = -Math.PI / 2 + 0.01;

  private viewMatrix: Float32Array;

  constructor(position: Vec3 = { x: 0, y: 0.5, z: 0 }) {
    this.position = { ...position };
    this.yaw = 0;
    this.pitch = 0;
    this.viewMatrix = new Float32Array(16);
  }

  rotate(deltaYaw: number, deltaPitch: number): void {
    this.yaw += deltaYaw;
    this.pitch += deltaPitch;

    // Clamp pitch to prevent gimbal lock
    this.pitch = Math.max(this.minPitch, Math.min(this.maxPitch, this.pitch));

    // Keep yaw in [-PI, PI] range
    while (this.yaw > Math.PI) this.yaw -= 2 * Math.PI;
    while (this.yaw < -Math.PI) this.yaw += 2 * Math.PI;
  }

  getForward(): Vec3 {
    // Forward direction based on yaw only (for movement)
    return {
      x: -Math.sin(this.yaw),
      y: 0,
      z: -Math.cos(this.yaw)
    };
  }

  getRight(): Vec3 {
    // Right direction (perpendicular to forward)
    return {
      x: Math.cos(this.yaw),
      y: 0,
      z: -Math.sin(this.yaw)
    };
  }

  getLookDirection(): Vec3 {
    // Full 3D look direction including pitch
    const cosPitch = Math.cos(this.pitch);
    return {
      x: -Math.sin(this.yaw) * cosPitch,
      y: Math.sin(this.pitch),
      z: -Math.cos(this.yaw) * cosPitch
    };
  }

  getViewMatrix(): Float32Array {
    // Build view matrix from position and rotation
    // This is essentially the inverse of the camera's world transform

    const cosPitch = Math.cos(this.pitch);
    const sinPitch = Math.sin(this.pitch);
    const cosYaw = Math.cos(this.yaw);
    const sinYaw = Math.sin(this.yaw);

    // Camera axes
    const rightX = cosYaw;
    const rightY = 0;
    const rightZ = -sinYaw;

    const upX = sinYaw * sinPitch;
    const upY = cosPitch;
    const upZ = cosYaw * sinPitch;

    const forwardX = -sinYaw * cosPitch;
    const forwardY = sinPitch;
    const forwardZ = -cosYaw * cosPitch;

    // Translation (negative because we're moving the world, not the camera)
    const tx = -(rightX * this.position.x + rightY * this.position.y + rightZ * this.position.z);
    const ty = -(upX * this.position.x + upY * this.position.y + upZ * this.position.z);
    const tz = -(-forwardX * this.position.x - forwardY * this.position.y - forwardZ * this.position.z);

    // Column-major order for WebGL
    this.viewMatrix[0] = rightX;
    this.viewMatrix[1] = upX;
    this.viewMatrix[2] = -forwardX;
    this.viewMatrix[3] = 0;

    this.viewMatrix[4] = rightY;
    this.viewMatrix[5] = upY;
    this.viewMatrix[6] = -forwardY;
    this.viewMatrix[7] = 0;

    this.viewMatrix[8] = rightZ;
    this.viewMatrix[9] = upZ;
    this.viewMatrix[10] = -forwardZ;
    this.viewMatrix[11] = 0;

    this.viewMatrix[12] = tx;
    this.viewMatrix[13] = ty;
    this.viewMatrix[14] = tz;
    this.viewMatrix[15] = 1;

    return this.viewMatrix;
  }

  setPosition(x: number, y: number, z: number): void {
    this.position.x = x;
    this.position.y = y;
    this.position.z = z;
  }
}
